import React, { useContext, useEffect, useState } from 'react'
import { Grid, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from '@mui/material';
import { createCampaignResponse } from '../../../models/axiosModel';
import dashboardStyles from './../../../assets/styles/componentStyles/callAnalysisStyles/callAnalysisDashboard.module.scss';
import style from "../../../assets/styles/componentStyles/whatsappCampaignStyles/whatsappCampaignStyle.module.scss";
import globalStyles from "../../../assets/styles/global.module.scss";
import { CAMPAIGN_STATUS, getColorFromStatus } from './campaignConstant';
import { formatDateAccordingToTimezoneUsingMoment, hasCampaignManageAccess } from '../../../utils/constants';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import feedbackStyles from "../../../assets/styles/componentStyles/feedbackStyles/feedbackStyle.module.scss";
import { WhatsappCampaignContext, WhatsappCampaignContextPayload } from '../../../context/whatsappCampaignContext';
import { useNavigate } from 'react-router-dom';
import GlobalTooltip from '../../utility/tooltip/globalTooltip';
import IconsColor from '../../utility/iconsColor';
import { primaryColor } from '../../../utils/colors';
import { isCampaignStatusPaused } from './utils';

interface CampaignListTableProps {
    flagDashboardPage?: boolean,
    campaignList: createCampaignResponse[] | null,
    tableInnerHeight: number,
    actionButtonsForCampaign?: (campaign: createCampaignResponse) => "-" | JSX.Element[],
    menuItem?: {
        label: string;
        action: (e: createCampaignResponse) => void;
        isDisable: (campaign: createCampaignResponse) => boolean;
    }[]
}

const CampaignListTable: React.FC<CampaignListTableProps> = (props) => {

    const { flagDashboardPage, campaignList, tableInnerHeight,
        actionButtonsForCampaign = (campaign) => { return "-" },
        menuItem = []
    } = props;

    const navigate = useNavigate();
    const { userDetail, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;
    const { setSelectedWhatsAppCampaign } = useContext(WhatsappCampaignContext) as WhatsappCampaignContextPayload;

    const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
    const [anchorEl, setAnchorEl] = useState<null | SVGSVGElement>(null);

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(50);

    const handleMenuOpen = (event: React.MouseEvent<SVGSVGElement, MouseEvent>, rowIndex: number) => {
        setCurrentRowIndex(rowIndex);
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setCurrentRowIndex(null);
        setAnchorEl(null);
    };

    useEffect(() => {
        if (campaignList && campaignList?.length > 0) {
            setPage(0);
        }
    }, [campaignList])

    const getCampaignExecutionTime = (campaignDetails: createCampaignResponse) => {
        const executionStatusName = campaignDetails?.campaign_execution_status_name?.toLowerCase() ?? '';

        const scheduledDateTime = (campaignDetails.campaign_message_sending_date && campaignDetails.campaign_message_sending_time)
            ? `${campaignDetails.campaign_message_sending_date} ${campaignDetails.campaign_message_sending_time}`
            : null;
        
        switch (executionStatusName) {
            case CAMPAIGN_STATUS["DRAFT"]?.toLowerCase():
                if (scheduledDateTime) {
                    return formatDateAccordingToTimezoneUsingMoment(scheduledDateTime, userDetail.timezone_hour);
                }
                return "-";
            case CAMPAIGN_STATUS['SCHEDULED']?.toLowerCase():
                return formatDateAccordingToTimezoneUsingMoment(scheduledDateTime || campaignDetails.updated_at_timestamp || campaignDetails?.created_at_timestamp, userDetail.timezone_hour);
            default:
                return formatDateAccordingToTimezoneUsingMoment(campaignDetails.updated_at_timestamp || scheduledDateTime || campaignDetails?.created_at_timestamp, userDetail.timezone_hour);
        }
 
    }

    const onClickOfCampaign = (campaign: createCampaignResponse)=> {
        setSelectedWhatsAppCampaign(null);
        const executionStatusName = campaign?.campaign_execution_status_name?.toLowerCase();
        switch (executionStatusName) {
            case CAMPAIGN_STATUS["DRAFT"].toLowerCase():
            case CAMPAIGN_STATUS["SCHEDULED"].toLowerCase():
                return navigate(`/whatsappcampaign/createnewcampaign?campaign_id=${campaign.campaign_id}`)
            case CAMPAIGN_STATUS["COMPLETED"].toLowerCase():
                return navigate(`/whatsappcampaign?campaign_id=${campaign.campaign_id}`)
            default:
                return navigate(`/whatsappcampaign?campaign_id=${campaign.campaign_id}`)
        }
    }

    const getPaginatedSortedCampaignList = (campaignList: createCampaignResponse[] | null) => {
        if(!Array.isArray(campaignList) || campaignList?.length === 0){
            return [];
        }

        return campaignList?.sort((a, b) => {
            const bDate = new Date(b.updated_at_timestamp ?? b.created_at_timestamp).getTime();
            const aDate = new Date(a.updated_at_timestamp ?? a.created_at_timestamp).getTime();
            
            return bDate - aDate;
        })?.slice((page * rowsPerPage), (page * rowsPerPage) + rowsPerPage);
    }

    const getTableHeaderTableCellClassNames = (isLastColumn: boolean, extraClasses?: string) => {
        return `${dashboardStyles.tableBodyHeadingFontStyle} ${!isLastColumn ? style.minWidthTableCell : dashboardStyles.viewButtonColumnHeadingStyle} ${extraClasses ? extraClasses : ''}`;
    }

    const getTableBodyTableCellClassNames = (isLastColumn: boolean, extraClasses?: string) => {
        return `${dashboardStyles.tableBodySubHeadingFontStyle} ${style.tableCellColor} ${!isLastColumn ? style.minWidthTableCell : `${dashboardStyles.viewButtonColumnStyle} ${globalStyles.textItemsEnd}`} ${extraClasses ? extraClasses : ''}`;
    }

    const shouldShowCampaignActionColumns = () => {
        if(!flagDashboardPage && hasCampaignManageAccess(apiRoutesPermissionAssignment, userDetail)){
            return true;
        }
        return false;
    }

    return (
        <>
            <Grid container className={`${globalStyles.flexColumn} ${globalStyles.gapTwo}`}>
                <Grid item className={`${globalStyles.fullwidth}`}>
                    <TableContainer className={style.campaignTableContainer} sx={{ maxHeight: `${tableInnerHeight}px !important` }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={`${getTableHeaderTableCellClassNames(false, style.stickyColumnHeader)}`}>Campaign Title</TableCell>
                                    <TableCell className={`${getTableHeaderTableCellClassNames(false)}`}>Template Name</TableCell>
                                    <TableCell className={`${getTableHeaderTableCellClassNames(false)}`}>Contact List</TableCell>
                                    <TableCell className={`${getTableHeaderTableCellClassNames(false)}`}>Updated At</TableCell>
                                    <TableCell className={`${getTableHeaderTableCellClassNames(false)}`}>Execution Time</TableCell>
                                    <TableCell className={`${getTableHeaderTableCellClassNames(false)}`}>Status</TableCell>
                                    {shouldShowCampaignActionColumns() &&
                                        <>
                                            <TableCell className={`${getTableHeaderTableCellClassNames(false)}`}>Action</TableCell>
                                            <TableCell className={`${getTableHeaderTableCellClassNames(true)}`}></TableCell>
                                        </>
                                    }
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getPaginatedSortedCampaignList(campaignList)?.length > 0 ?
                                    getPaginatedSortedCampaignList(campaignList)?.map((campaign: createCampaignResponse, ind: number) => {
                                        return (
                                            <TableRow className={dashboardStyles.cellRow} key={ind}>
                                                <TableCell
                                                    className={`${getTableBodyTableCellClassNames(false, `${style.stickyColumn} ${style.campaignTitleTableCell}`)}`}
                                                    sx={{ borderLeft: `3px solid ${getColorFromStatus(campaign.campaign_execution_status_name)}` }}
                                                    onClick={() => onClickOfCampaign(campaign)}
                                                >
                                                    {campaign.campaign_title}
                                                </TableCell>
                                                <TableCell className={`${getTableBodyTableCellClassNames(false)}`}>
                                                    {campaign?.whatsapp_template_id ? 
                                                        <a href={`/channels/viewintegration/whatsapp/templatelibrary/modifyTemplate?id=${campaign?.meta_integration_whatsapp_id}&template_id=${campaign?.whatsapp_template_id}`} target="_blank">
                                                            {campaign?.whatsapp_template_display_name ?? campaign?.whatsapp_template_name}
                                                        </a>
                                                        :
                                                        "-"
                                                    }
                                                </TableCell>
                                                <TableCell className={`${getTableBodyTableCellClassNames(false)}`}>
                                                    <>
                                                        <a href={`/contacts?contact_list_id=${campaign?.contact_list_id}`} target="_blank">
                                                            {campaign?.contact_list_name}
                                                        </a>
                                                         {` (Contacts - ${campaign?.total_campaign_contact})`}
                                                    </>
                                                </TableCell>
                                                <TableCell className={`${getTableBodyTableCellClassNames(false)}`}>{formatDateAccordingToTimezoneUsingMoment(campaign.updated_at_timestamp ? campaign.updated_at_timestamp : campaign.created_at_timestamp, userDetail.timezone_hour)}</TableCell>
                                                <TableCell className={`${getTableBodyTableCellClassNames(false)}`}>{getCampaignExecutionTime(campaign)}</TableCell>
                                                <TableCell className={`${getTableBodyTableCellClassNames(false)}`}>
                                                    <Grid container gap={1} className={globalStyles.displayFlex}>
                                                        <span className={`${style.statusTableCell}`} style={{ border: `1px solid ${getColorFromStatus(campaign.campaign_execution_status_name)}`, color: getColorFromStatus(campaign.campaign_execution_status_name) }}>
                                                            {campaign.campaign_execution_status_name}
                                                        </span>
                                                        {isCampaignStatusPaused(campaign.campaign_execution_status_name) && 
                                                            <GlobalTooltip
                                                                title={campaign?.campaign_pause_reason ?? ''}
                                                                placement="top"
                                                                arrow
                                                            >
                                                                <span className={`${globalStyles.clickable} ${globalStyles.displayFlex}`}>
                                                                    <IconsColor iconName="info" fillColor={primaryColor} height="16" width="16" />
                                                                </span>
                                                            </GlobalTooltip>
                                                        }
                                                    </Grid>
                                                </TableCell>
                                                {shouldShowCampaignActionColumns() &&
                                                    <>
                                                        <TableCell className={`${getTableBodyTableCellClassNames(false)}`}>
                                                            <Grid container gap={1} flexWrap={"nowrap"}>
                                                                {actionButtonsForCampaign(campaign)}
                                                            </Grid>
                                                        </TableCell>
                                                        <TableCell className={`${getTableBodyTableCellClassNames(true)}`}>
                                                            <MoreVertIcon
                                                                className={globalStyles.clickable}
                                                                onClick={(e: React.MouseEvent<SVGSVGElement, MouseEvent>) => { handleMenuOpen(e, campaign.campaign_id) }}
                                                            />
                                                            <Menu
                                                                anchorEl={anchorEl}
                                                                keepMounted
                                                                open={Boolean(anchorEl) && campaign.campaign_id === currentRowIndex}
                                                                onClose={handleMenuClose}
                                                            >
                                                                {menuItem.map((item, index) => {
                                                                    return (
                                                                        <MenuItem
                                                                            className={`${globalStyles.menuItemStyle} ${globalStyles.secondaryFontSize}`}
                                                                            key={index}
                                                                            onClick={(e) => { handleMenuClose(); item.action(campaign); }}
                                                                            disabled={item.isDisable(campaign) ? true : false}
                                                                        >
                                                                            <span>
                                                                                {item.label}
                                                                            </span>
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Menu>
                                                        </TableCell>
                                                    </>
                                                }
                                            </TableRow>
                                        );
                                    })
                                    :
                                    <TableRow className={dashboardStyles.cellRow}>
                                        <TableCell colSpan={12} className={globalStyles.textCenter}>
                                            No Data Available
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                {(campaignList && campaignList?.length > 0) &&
                    <Grid item className={globalStyles.textItemsEnd}>
                        <Grid container className={globalStyles.contentEnd}>
                            <Grid item className={style.campaignTablePaginationGrid}>
                                <TablePagination
                                    component="div"
                                    className={`${feedbackStyles.feedbackTablePaginationStyle}`}
                                    count={campaignList?.length}
                                    rowsPerPageOptions={[10, 25, 50, 100, 1000]}
                                    labelRowsPerPage="Rows per page:"
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                                    onPageChange={(e, newPage) => setPage(newPage)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                }
            </Grid>
        </>
    )
}

export default CampaignListTable
